import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
      setTokenValid(res.data.valid);
      setUserEmail(res.data.email || "");
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password?token=${token}&new_password=${encodeURIComponent(password)}`);
      setSuccess(true);
      toast.success("Mot de passe réinitialisé avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="bg-card border shadow-lg">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="mx-auto">
              <img 
                src="/images/logo-betaye.jpg" 
                alt="Be-Taye" 
                className="w-28 h-auto mx-auto"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">
                {success ? "Mot de passe réinitialisé" : !tokenValid ? "Lien invalide" : "Nouveau mot de passe"}
              </h1>
              {tokenValid && !success && userEmail && (
                <p className="text-sm text-muted-foreground mt-1">
                  Pour {userEmail}
                </p>
              )}
            </div>
          </CardHeader>
          
          <CardContent>
            {success ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-foreground font-medium">Succès !</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.
                  </p>
                </div>
                <Button
                  className="w-full btn-primary bg-primary text-primary-foreground mt-4"
                  onClick={() => navigate("/login")}
                  data-testid="go-to-login"
                >
                  Se connecter
                </Button>
              </div>
            ) : !tokenValid ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-red-50 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <div>
                  <p className="text-foreground font-medium">Lien invalide ou expiré</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Ce lien de réinitialisation n'est plus valide. Veuillez en demander un nouveau.
                  </p>
                </div>
                <Link to="/forgot-password">
                  <Button className="w-full btn-primary bg-primary text-primary-foreground mt-4">
                    Demander un nouveau lien
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm text-muted-foreground">
                    Nouveau mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-sm text-muted-foreground">
                    Confirmer le mot de passe
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirm"
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                  )}
                </div>
                
                <Button
                  type="submit"
                  className="w-full btn-primary bg-primary text-primary-foreground font-semibold h-11"
                  disabled={loading || password !== confirmPassword}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></span>
                      Réinitialisation...
                    </span>
                  ) : "Réinitialiser le mot de passe"}
                </Button>
              </form>
            )}
            
            {!success && (
              <Link 
                to="/login" 
                className="flex items-center justify-center gap-2 mt-6 text-sm text-primary hover:underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à la connexion
              </Link>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
